import { LuExternalLink } from "react-icons/lu";
import TextField from "../../../../common/TextField";
import RegisterNetwork from "./RegisterNetwork";
import { isNetworkConfigComplete } from "../../../../../../utils/planManager/networkConfigurationUtils.js";
import "./NetworksTab.scss";

/**
 * External master network scheme reference opened from the Networks tab.
 * @constant {string}
 */
export const MASTER_NETWORK_SCHEME_URL =
    "https://centivo.atlassian.net/l/cp/QAxDTwtC";

/**
 * Networks tab: the client name field, the master network scheme reference, and
 * one {@link RegisterNetwork} block per configured network. Presentational —
 * all edits flow up through callbacks; owns no state.
 *
 * @param {object} props
 * @param {string} props.clientName
 * @param {Array<Object>} props.networkConfigs
 * @param {Record<string, Array<string>>} props.seriesOptions
 * @param {(value: string) => void} props.onClientNameChange
 * @param {(index: number, field: string, value: *) => void} props.onFieldChange
 * @param {(index: number, directoryType: string) => void} props.onDirectoryTypeChange
 * @param {(index: number, include: boolean) => void} props.onIncludePreEnrollmentChange
 * @param {(index: number, seriesIndex: number, code: string) => void} props.onSeriesChange
 * @param {(index: number, code: string) => void} props.onPreferredChange
 * @param {(index: number, field: string, value: *) => void} props.onExternalFieldChange
 * @param {(index: number) => void} props.onDeleteClick
 * @param {() => void} props.onAddClick
 * @returns {import('react').JSX.Element}
 */
const NetworksTab = ({
    clientName,
    networkConfigs,
    seriesOptions,
    onClientNameChange,
    onFieldChange,
    onDirectoryTypeChange,
    onIncludePreEnrollmentChange,
    onSeriesChange,
    onPreferredChange,
    onExternalFieldChange,
    onDeleteClick,
    onAddClick,
}) => {
    const lastIndex = networkConfigs.length - 1;

    return (
        <div
            id="config-panel-networks"
            role="tabpanel"
            aria-labelledby="config-tab-networks"
            className="config-networks-tab"
        >
            <div className="config-networks-tab__top">
                <TextField
                    id="client-name"
                    label="Client name"
                    placeholder="Create client name"
                    value={clientName}
                    onChange={onClientNameChange}
                />
                <a
                    className="config-networks-tab__scheme-link"
                    href={MASTER_NETWORK_SCHEME_URL}
                    target="_blank"
                    rel="noreferrer"
                >
                    View master network scheme
                    <LuExternalLink aria-hidden size={16} />
                </a>
            </div>

            {networkConfigs.map((config, index) => (
                <RegisterNetwork
                    key={config.rowId}
                    index={index}
                    config={config}
                    seriesOptions={seriesOptions}
                    canDelete={Boolean(config.directoryType) || index > 0}
                    showAddButton={
                        index === lastIndex && isNetworkConfigComplete(config)
                    }
                    onFieldChange={onFieldChange}
                    onDirectoryTypeChange={onDirectoryTypeChange}
                    onIncludePreEnrollmentChange={onIncludePreEnrollmentChange}
                    onSeriesChange={onSeriesChange}
                    onPreferredChange={onPreferredChange}
                    onExternalFieldChange={onExternalFieldChange}
                    onDeleteClick={onDeleteClick}
                    onAddClick={onAddClick}
                />
            ))}
        </div>
    );
};

export default NetworksTab;
